import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PaperPlaneRight, CircleNotch, Camera, X, ImageSquare } from "@phosphor-icons/react";
import { fireLeadConversion } from "@/adsConfig";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const EMPTY = { name: "", phone: "", email: "", preferredTime: "", address: "", details: "", company: "", businessType: "", pickupHours: "" };
export const BUSINESS_TYPES = ["Office", "Retail store", "Restaurant", "Property manager / HOA / Apartments", "Warehouse / Storage", "Contractor / Job site", "Other"];

export default function QuoteForm({ variant = "hero" }) {
  const isBiz = variant === "commercial";
  const [form, setForm] = useState(EMPTY);
  const [photos, setPhotos] = useState([]); // {id, previewUrl, path, url, filename, uploading}
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image.`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is over 10MB.`);
        continue;
      }
      const id = `${Date.now()}-${Math.random()}`;
      const previewUrl = URL.createObjectURL(file);
      setPhotos((p) => [...p, { id, previewUrl, uploading: true }]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPhotos((p) =>
          p.map((ph) =>
            ph.id === id ? { ...ph, uploading: false, path: data.path, url: data.url, filename: data.filename } : ph
          )
        );
      } catch (err) {
        toast.error(`Couldn't upload ${file.name}.`);
        setPhotos((p) => p.filter((ph) => ph.id !== id));
      }
    }
  };

  const removePhoto = (id) => setPhotos((p) => p.filter((ph) => ph.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please fill in your name and phone.");
      return;
    }
    if (isBiz && !form.company) {
      toast.error("Please add your company name.");
      return;
    }
    if (photos.some((ph) => ph.uploading)) {
      toast.error("Please wait for photos to finish uploading.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        address: form.address,
        details: form.details,
        preferred_time: form.preferredTime,
      };
      if (form.email) payload.email = form.email;
      if (isBiz) {
        payload.service = "Commercial Junk Removal";
        payload.company = form.company;
        payload.business_type = form.businessType;
        payload.pickup_hours = form.pickupHours;
      }
      payload.photos = photos
        .filter((ph) => ph.path)
        .map((ph) => ({ path: ph.path, url: ph.url, filename: ph.filename }));
      await axios.post(`${API}/quotes`, payload);
      fireLeadConversion();
      toast.success("Quote request sent! We'll call you back fast. 🚛");
      setForm(EMPTY);
      setPhotos([]);
    } catch (err) {
      toast.error("Something went wrong. Call/Text us at (619) 999-3565.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-[#E4E4E7] px-4 py-3 text-[#0A0A0A] placeholder:text-[#A1A1AA] text-sm focus:outline-none focus:ring-2 focus:ring-[#46A01E] focus:border-transparent transition-shadow rounded-sm";

  return (
    <form onSubmit={submit} data-testid={`quote-form-${variant}`} className="space-y-3">
      {isBiz && (
        <>
          <input data-testid="quote-company-input" aria-label="Company name" className={inputCls} placeholder="Company name *"
            value={form.company} onChange={(e) => update("company", e.target.value)} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <select data-testid="quote-business-type-select" aria-label="Business type" className={inputCls}
              value={form.businessType} onChange={(e) => update("businessType", e.target.value)}>
              <option value="">Business type</option>
              {BUSINESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select data-testid="quote-pickup-hours-select" aria-label="Preferred pickup hours" className={inputCls}
              value={form.pickupHours} onChange={(e) => update("pickupHours", e.target.value)}>
              <option value="">Preferred pickup hours</option>
              <option value="During business hours">During business hours</option>
              <option value="After hours">After hours</option>
              <option value="Either">Either works</option>
            </select>
          </div>
        </>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          data-testid="quote-name-input"
          aria-label="Full name"
          className={inputCls}
          placeholder="Full name *"
          value={form.name}
          onChange={(e) => update("name", e.target.value)}
        />
        <input
          data-testid="quote-phone-input"
          aria-label="Phone number"
          className={inputCls}
          placeholder="Phone number *"
          value={form.phone}
          onChange={(e) => update("phone", e.target.value)}
        />
      </div>
      <input
        data-testid="quote-email-input"
        aria-label="Email (optional)"
        className={inputCls}
        placeholder="Email (optional)"
        value={form.email}
        onChange={(e) => update("email", e.target.value)}
      />
      <input
        data-testid="quote-preferred-time-input"
        aria-label="When would you like the service done?"
        className={inputCls}
        placeholder="When would you like the service done? Day or Date, and time."
        value={form.preferredTime}
        onChange={(e) => update("preferredTime", e.target.value)}
      />
      <input
        data-testid="quote-address-input"
        aria-label="Address or ZIP code"
        className={inputCls}
        placeholder="Address / ZIP (San Diego County)"
        value={form.address}
        onChange={(e) => update("address", e.target.value)}
      />
      <textarea
        data-testid="quote-details-input"
        aria-label="Tell us about the items"
        className={`${inputCls} resize-none`}
        rows={2}
        placeholder="Tell us about the items..."
        value={form.details}
        onChange={(e) => update("details", e.target.value)}
      />

      {/* Photo upload */}
      <div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFiles}
          className="hidden"
          data-testid="quote-photo-input"
        />
        <button
          type="button"
          data-testid="quote-photo-button"
          onClick={() => fileRef.current?.click()}
          className="w-full flex items-center justify-center gap-2 border border-dashed border-[#46A01E] text-[#15306E] bg-[#F4FAF0] hover:bg-[#EAF4E3] px-4 py-3 rounded-sm text-sm font-semibold transition-colors"
        >
          <Camera size={18} weight="duotone" className="text-[#46A01E]" />
          Add photos of your junk (optional)
        </button>

        {photos.length > 0 && (
          <div className="grid grid-cols-4 gap-2 mt-3" data-testid="quote-photo-previews">
            {photos.map((ph) => (
              <div key={ph.id} className="relative aspect-square rounded-sm overflow-hidden border border-[#E4E4E7] group">
                <img src={ph.previewUrl} alt={ph.filename || "upload"} className="w-full h-full object-cover" />
                {ph.uploading && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <CircleNotch size={20} weight="bold" className="animate-spin text-white" />
                  </div>
                )}
                <button
                  type="button"
                  data-testid={`remove-photo-${ph.id}`}
                  aria-label="Remove photo"
                  onClick={() => removePhoto(ph.id)}
                  className="absolute top-1 right-1 bg-black/60 hover:bg-[#46A01E] text-white rounded-full p-0.5 transition-colors"
                >
                  <X size={13} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        )}
        {photos.length > 0 && (
          <p className="text-xs text-[#52525B] mt-2 flex items-center gap-1">
            <ImageSquare size={13} weight="fill" className="text-[#46A01E]" />
            {photos.length} photo{photos.length > 1 ? "s" : ""} attached — helps us quote faster.
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="quote-submit-button"
        className="group w-full bg-[#46A01E] hover:bg-[#3A8418] text-white font-display font-extrabold uppercase tracking-wide text-sm py-4 rounded-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
      >
        {loading ? (
          <CircleNotch size={20} weight="bold" className="animate-spin" />
        ) : (
          <>
            {isBiz ? "Get My Business Quote" : "Get My Free Quote"}
            <PaperPlaneRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
          </>
        )}
      </button>
      <p className="text-xs text-[#52525B] text-center">
        No obligation · Upfront pricing · Same-day service available
      </p>
    </form>
  );
}
